import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building2,
  GraduationCap,
  Briefcase,
  CreditCard,
  FileText,
  Eye,
  Heart,
  Trophy,
  IdCard,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { EmptyState, StatusBadge, RoleBadge } from '@/components/shared'
import { employeeService } from '@/services/employeeService'
import { documentService } from '@/services/documentService'
import { useAuthStore } from '@/store/authStore'
import { API_BASE_URL } from '@/lib/apiBase'

const EmployeeDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const canViewDetails = ['admin', 'hr'].includes(user?.role)

  // Fetch employee details
  const { data: employeeData, isLoading, error } = useQuery(
    ['employeeDetails', id],
    () => employeeService.getEmployeeById(id),
    {
      enabled: !!id && canViewDetails,
      onError: () => {
        toast.error('Failed to fetch employee details')
      }
    }
  )

  // Fetch employee documents
  const { data: documentsData, isLoading: documentsLoading } = useQuery(
    ['employeeDocuments', id],
    () => documentService.getEmployeeDocuments(id),
    {
      enabled: !!id && canViewDetails,
    }
  )

  const employee = employeeData?.data?.employee
  const documents = documentsData?.data?.documents || []

  const handleViewDocument = (document) => {
    window.open(document.view_url, '_blank', 'noopener')
  }

  const getProfilePictureUrl = () => {
    if (!employee?.profile_picture_path) return null
    
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage)
        const token = authData.state?.token
        if (token) {
          return `${API_BASE_URL}/employees/${id}/picture?token=${encodeURIComponent(token)}`
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error)
      }
    }
    return null
  }

  const InfoItem = ({ icon: Icon, label, value, iconColor = 'text-muted-foreground' }) => (
    <div className="flex items-start gap-3 py-2">
      <div className={`p-1.5 rounded-md bg-muted ${iconColor}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground truncate">{value || 'Not provided'}</p>
      </div>
    </div>
  )

  if (!canViewDetails) {
    return (
      <div>
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon="users"
              title="Access Denied"
              description="You don't have permission to view employee details."
              actionLabel="Go Back"
              onAction={() => navigate(-1)}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !employee) {
    return (
      <div>
        <Button variant="ghost" size="sm" className="mb-4 h-8 px-3 text-sm font-normal" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-3.5 w-3.5" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon="users"
              title="Employee Not Found"
              description="The employee you're looking for doesn't exist."
              actionLabel="Go Back"
              onAction={() => navigate('/employees')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="h-8 w-8">
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Employee Details</h1>
          <p className="text-sm text-muted-foreground">View complete employee information</p>
        </div>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <Avatar className="h-20 w-20 border-2 border-border">
              <AvatarImage src={getProfilePictureUrl()} />
              <AvatarFallback className="text-xl bg-primary/10 text-primary font-semibold">
                {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2 className="text-2xl font-semibold text-foreground">{employee.first_name} {employee.last_name}</h2>
                <div className="flex items-center gap-2">
                  <StatusBadge status={employee.status} />
                  <RoleBadge role={employee.user?.role} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <IdCard className="h-4 w-4" />
                  {employee.employee_id}
                </span>
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" />
                  {employee.department}
                </span>
                <span className="flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4" />
                  {employee.job_title}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <User className="h-4 w-4 text-primary" />
              </div>
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoItem icon={Mail} label="Email" value={employee.user?.email} />
            <InfoItem icon={Phone} label="Contact" value={employee.contact_number} />
            <InfoItem icon={Building2} label="Department" value={employee.department} />
            <InfoItem icon={Trophy} label="Job Title" value={employee.job_title} />
            <InfoItem 
              icon={Calendar} 
              label="Date of Joining" 
              value={employee.date_of_joining ? dayjs(employee.date_of_joining).format('MMM DD, YYYY') : null}
            />
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-pink-100">
                <Heart className="h-4 w-4 text-pink-600" />
              </div>
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoItem 
              icon={Calendar} 
              label="Date of Birth" 
              value={employee.date_of_birth ? dayjs(employee.date_of_birth).format('MMM DD, YYYY') : null}
            />
            <InfoItem icon={User} label="Gender" value={employee.gender} />
            <InfoItem icon={Heart} label="Marital Status" value={employee.marital_status} />
            <InfoItem icon={Phone} label="Emergency Contact" value={employee.emergency_contact_name} />
            <InfoItem icon={Phone} label="Emergency Number" value={employee.emergency_contact_number} />
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-orange-100">
                <MapPin className="h-4 w-4 text-orange-600" />
              </div>
              Address
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employee.addresses && employee.addresses.length > 0 ? (
              <div className="space-y-3">
                {employee.addresses.map((address, index) => (
                  <div key={index} className="p-3 bg-muted/50 rounded-lg border border-border">
                    <Badge variant="outline" className="mb-2 text-xs">{address.address_type}</Badge>
                    <p className="text-sm font-medium text-foreground">
                      {address.address_line_1}
                      {address.address_line_2 && `, ${address.address_line_2}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {address.city}, {address.state} - {address.pincode}
                    </p>
                    <p className="text-sm text-muted-foreground">{address.country}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No address information available</p>
            )}
          </CardContent>
        </Card>

        {/* Education */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-purple-100">
                <GraduationCap className="h-4 w-4 text-purple-600" />
              </div>
              Education
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoItem icon={GraduationCap} label="Qualification" value={employee.highest_qualification} />
            <InfoItem icon={Building2} label="University" value={employee.university} />
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-1">Skills</p>
              <p className="text-sm font-medium text-foreground">{employee.skills || 'Not provided'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Bank Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-emerald-100">
                <CreditCard className="h-4 w-4 text-emerald-600" />
              </div>
              Bank Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoItem icon={User} label="Account Holder" value={employee.account_holder_name} />
            <InfoItem icon={Building2} label="Bank Name" value={employee.bank_name} />
            <InfoItem icon={CreditCard} label="Account Number" value={employee.account_number} />
            <InfoItem icon={CreditCard} label="IFSC Code" value={employee.ifsc_code} />
            <InfoItem icon={CreditCard} label="PAN Number" value={employee.pan_number} />
          </CardContent>
        </Card>

        {/* Work Experience */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-amber-100">
                <Briefcase className="h-4 w-4 text-amber-600" />
              </div>
              Work Experience
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employee.workExperiences && employee.workExperiences.length > 0 ? (
              <div className="space-y-3">
                {employee.workExperiences.map((exp, index) => (
                  <div key={index} className="p-3 bg-muted/50 rounded-lg border border-border">
                    <p className="text-sm font-semibold text-foreground">{exp.position}</p>
                    <p className="text-sm text-primary">{exp.organization_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {dayjs(exp.start_date).format('MMM YYYY')} - {exp.end_date ? dayjs(exp.end_date).format('MMM YYYY') : 'Present'}
                    </p>
                    {exp.location && <p className="text-xs text-muted-foreground">{exp.location}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No work experience information available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Documents */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="p-1.5 rounded-lg bg-blue-100">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documentsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-28" />
              ))}
            </div>
          ) : documents.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {documents.map((doc) => (
                <Card 
                  key={doc.id} 
                  className="cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all" 
                  onClick={() => handleViewDocument(doc)}
                >
                  <CardContent className="p-4 text-center">
                    <div className="p-2 rounded-lg bg-primary/10 w-fit mx-auto mb-2">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-sm font-medium truncate text-foreground">{doc.document_type}</p>
                    <p className="text-xs text-muted-foreground truncate mb-2">{doc.file_name}</p>
                    <Button variant="outline" size="sm" className="h-8 px-3 text-sm font-normal">
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      View
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No documents available</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default EmployeeDetails
